import { BaseLocationType } from '../models/BaseLocation';
import { MediaTypeEnum } from '../gql/schema';
import type { BaseInstanceConfig } from '../models/BaseInstanceConfig';
import { BaseTourDifficulty } from '../models/BaseTourDifficulty';
import { WhlConsentManagerType } from '@models/WhlConsentManagerType';
import { ConsentVendor } from '@utils/consentManager/consent-manager';
import { mediaLicenseIconMapping } from '@utils/mediaLicense';

enum FWTM_MEDIA_TYPES {
  STANDARD = 1,
  MIGRIERT = 3,
  HEADERBILD = 4,
}

enum FWTM_ADDRESSBASE_PRODUCTLINES {
  // Generelle Merkmale
  BARRIEREFREI = 380,
  NACHTSAME_EINRICHTUNG = 386,
  PARTNER_DER_STADT_FREIBURG = 385,
  // Wetterbezogene Infos
  WETTERUNABHAENGIG = 371,
}

enum FWTM_TOUR_CATEGORIES {
  AKTIVTOUREN = 3,
  MOTORISIERTE_TOUREN = 4,
  PISTE = 5,
  RADTOUREN = 6,
  RUNDGANG = 7,
  WANDERUNG = 8,
  WASSERTOUREN = 9,
  WINTERTOUREN = 10,

  // Aktivtouren
  INLINE_SKATING_TOUR = 11,
  KLETTERTOUR = 12,
  NORDIC_WALKING_TOUR = 13,
  REITTOUR = 14,

  // Motorisierte Touren
  FERIENSTRASSE = 17,
  MOTORRADTOUR = 15,
  OFFROADTOUR = 18,
  WOHNMOBILTOUR = 16,

  // Piste
  BUCKELPISTE = 20,
  SKIPISTE = 19,
  SKIROUTE = 22,
  UEBUNGHANG = 21,
  ZIEHWEG = 23,

  // Radtouren
  GRAVELTOUR = 25,
  MTBTOUR = 26,
  RADFERNWEG = 24,
  RADTOUR = 27,
  RENNRADTOUR = 28,

  // Rundgang
  SPAZIERGANG = 29,

  // Wanderung
  ALPINTOUR = 35,
  BERGTOUR = 37,
  FERNWANDERWEG = 32,
  KLETTERSTEG = 31,
  LEHRPFAD = 36,
  PILGERWEG = 33,

  // Wassertouren
  BOOTSTOUR = 39,
  KAJAKTOUR = 41,
  SEGELTOUR = 40,

  // Wintertouren
  LOIPE = 42,
  RODELABFAHRT = 46,
  SCHNEESCHUHTOUR = 43,
  SKITOUR = 44,
  WINTERWANDERWEG = 45,
}

export const CONFIG: Omit<BaseInstanceConfig, 'instanceId'> = {
  accommodation: {
    detail: {
      hero: {
        showDescriptionType: 'combination',
      },
    },
  },
  defaultWidgetId: 1,
  defaults: {
    hero: {
      filter: {
        mediaType: [MediaTypeEnum.Image],
        mediaProfile: { eq: FWTM_MEDIA_TYPES.HEADERBILD },
      },
      fallback: [
        {
          mediaType: [MediaTypeEnum.Image],
          mediaProfile: { eq: FWTM_MEDIA_TYPES.STANDARD },
        },
      ],
      mediaLicenseIconMapping: mediaLicenseIconMapping,
    },
    gallery: {
      filter: {
        or: [
          {
            mediaType: [MediaTypeEnum.Image],
            mediaProfile: {
              oneOf: [FWTM_MEDIA_TYPES.STANDARD, FWTM_MEDIA_TYPES.HEADERBILD],
            },
          },
          { mediaType: [MediaTypeEnum.Video] },
        ],
      },
    },
    list: {
      filter: {
        mediaType: [MediaTypeEnum.Image],
        mediaProfile: {
          oneOf: [FWTM_MEDIA_TYPES.HEADERBILD, FWTM_MEDIA_TYPES.STANDARD],
        },
      },
    },
    article: {
      contact: {
        type: 'contact',
        showStops: false,
      },
    },
  },
  event: {
    list: {
      teaser: {
        list: {
          showContactAddress: false,
        },
      },
      list: {
        standard: {
          showCategory: true,
        },
      },
    },
    overview: {
      teaser: {
        box: {
          showContactAddress: false,
        },
      },
      slider: {
        teaser: {
          showCategory: true,
          showContactAddress: true,
        },
      },
    },
    detail: {
      teaser: {
        box: {
          showContactAddress: false,
        },
      },
      article: {
        contact: {
          productlinesWhitelist: [
            FWTM_ADDRESSBASE_PRODUCTLINES.BARRIEREFREI,
            FWTM_ADDRESSBASE_PRODUCTLINES.WETTERUNABHAENGIG,
          ],
        },
        contributor: {
          productlinesWhitelist: [
            FWTM_ADDRESSBASE_PRODUCTLINES.NACHTSAME_EINRICHTUNG,
            FWTM_ADDRESSBASE_PRODUCTLINES.PARTNER_DER_STADT_FREIBURG,
          ],
        },
      },
      hero: {
        showCategoryInfoTags: true,
        showDescriptionType: 'combination',
      },
      productlineIcons: {
        [FWTM_ADDRESSBASE_PRODUCTLINES.BARRIEREFREI]: 'tabler:accessible-filled',
        [FWTM_ADDRESSBASE_PRODUCTLINES.NACHTSAME_EINRICHTUNG]: 'tabler:moon-filled',
        [FWTM_ADDRESSBASE_PRODUCTLINES.PARTNER_DER_STADT_FREIBURG]: 'tabler:shield-check-filled',
        [FWTM_ADDRESSBASE_PRODUCTLINES.WETTERUNABHAENGIG]: 'mynaui:cloud-sun-rain-solid',
      },
    },
    categoryInfoTagBlacklist: RegExp('(Sonstige|^Weitere.*)'),
  },
  footerContact: {
    name: 'Freiburg Wirtschaft Touristik und Messe GmbH & Co. KG',
    street: 'Neuer Messplatz',
    streetNo: '3',
    zipcode: '79108',
    city: 'Freiburg',
    email: 'info@visit.freiburg.de',
  },
  gastronomy: {
    detail: {
      hero: {
        showDescriptionType: 'combination',
      },
    },
  },
  global: {
    consentManagerUILink: 'javascript:UC_UI.showSecondLayer()',
  },
  graphqlBearerToken:
    'eyJraWQiOiJpbXgtY2RhIiwidHlwIjoiSldUIiwiYWxnIjoiUlMyNTYifQ.eyJpc3MiOiJodHRwczpcL1wvZnJlaWJ1cmcuaW14cGxhdGZvcm0uZGVcL29hdXRoIiwic3ViIjoiODkiLCJsbmFtZSI6IndzLndoaXRlbGFiZWwtd2lkZ2V0cyJ9.gg4QFEiSaiBDAUj0bmiAvMgJ__Fm7zWG6TJm3kxROuxOcttkuHDCmxy97vd6mFh6CJLH0dkmnYNpDoG3T6YdzJSbb1AF04MWBOi9siGtwwvijeXID4Ao4Jk-3RwRRkLRz1AmxaQRlXKT4oOMI_TJEnFtaLMywG7DW4tiycNkKzFvwtovl7w3UHBRwd63ZtaTsUCs0opH9APCkb8SgS48ibL-eBbBC4I_OxA8oVW8NnbT90JqkK3Bz05cuUfbYXfNXwRJpOCKuIqapAFdsJtpxj7qtBNTd1uf-Bav2ipwLXdo8yvJYswGR4kgLhBkZmnUxc4eY-SzsBrI_u_ZMGXKZA',
  graphqlEndpoint: `https://content-delivery.imxplatform.de/fwtm/imxplatform`,
  map: {
    defaultCoords: {
      // freiburg
      latitude: 47.996014049603474,
      longitude: 7.8495559643174255,
    },
    smartScroll: true,
  },
  pageheader: {
    search: {
      locationSearchWhitelist: [BaseLocationType.ADDRESS_POI_GROUP, BaseLocationType.LOCATION, BaseLocationType.REGION],
    },
  },
  poi: {
    detail: {
      hero: {
        showDescriptionType: 'combination',
      },
    },
  },
  portalConsentManager: {
    type: WhlConsentManagerType.USER_CENTRICS,
    config: {
      consentManagerId: 'd5rwSlSs3',
      vendorIds: {
        [ConsentVendor.MAPTOOLKIT]: 'qySmlP-Vg',
        [ConsentVendor.YOUTUBE]: 'BJz7qNsdj-7',
        [ConsentVendor.GTAG]: 'BJ59EidsWQ',
      },
    } satisfies ConsentManagerConfig,
  },
  scripts: [
    {
      innerHTML: `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-M3X6QJD');`,
      vendorInfo: 'Google Tag Manager' /** vendor id is name of service  */,
    },
  ],
  tour: {
    detail: {
      hero: {
        showDescriptionType: 'combination',
      },
    },
    categoryIcons: {
      [FWTM_TOUR_CATEGORIES.AKTIVTOUREN]: 'ion:active',
      [FWTM_TOUR_CATEGORIES.MOTORISIERTE_TOUREN]: 'ph:motorcycle-fill',
      [FWTM_TOUR_CATEGORIES.PISTE]: 'lets-icons:winter',
      [FWTM_TOUR_CATEGORIES.RADTOUREN]: 'ion:bicycle',
      [FWTM_TOUR_CATEGORIES.RUNDGANG]: 'icon-park-solid:turn-around',
      [FWTM_TOUR_CATEGORIES.WANDERUNG]: 'material-symbols:hiking',
      [FWTM_TOUR_CATEGORIES.WASSERTOUREN]: 'ion:boat-outline',
      [FWTM_TOUR_CATEGORIES.WINTERTOUREN]: 'lets-icons:winter',
    },
    difficultyMapping: {
      1: BaseTourDifficulty.EASY,
      2: BaseTourDifficulty.EASY,
      3: BaseTourDifficulty.MEDIUM,
      4: BaseTourDifficulty.HARD,
      5: BaseTourDifficulty.HARD,
    },
  },
};
